import type {
  InspectionTemplate,
  TemplateItem,
  TemplateSection,
} from 'inspection-types';

import { cloneTemplateSection } from './inspectionTemplateCustomizeHelpers';

export type BaselineDraftCustomizeProjection = {
  sortedSections: TemplateSection[];
  removedOverlayItemIds: ReadonlySet<string>;
  removedSectionPresentationIds: ReadonlySet<string>;
};

function sortByOrder<T extends { order: number }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => a.order - b.order);
}

/**
 * When `currentInspectionTemplate` returns both `baselineTemplate` and `template`,
 * derive the accordion rows: baseline order/striking-through rows missing from draft.
 *
 * Draft-only sections (present on `draft` only) append after baseline-ordered sections.
 */
export function projectInspectionTemplateCustomizeFromBaselineDraft(
  baseline: InspectionTemplate,
  draft: InspectionTemplate
): BaselineDraftCustomizeProjection {
  const removedOverlayItemIds = new Set<string>();
  const removedSectionPresentationIds = new Set<string>();

  const draftSectionById = new Map(
    draft.sections.map(section => [section.id, section])
  );
  const baselineSectionIds = new Set(baseline.sections.map(s => s.id));

  const sortedSections: TemplateSection[] = [];

  for (const baselineSection of sortByOrder(baseline.sections)) {
    const draftSection = draftSectionById.get(baselineSection.id);

    if (!draftSection) {
      removedSectionPresentationIds.add(baselineSection.id);
      for (const item of baselineSection.items) {
        removedOverlayItemIds.add(item.id);
      }
      sortedSections.push(cloneTemplateSection(baselineSection));
      continue;
    }

    const draftItemById = new Map(draftSection.items.map(item => [item.id, item]));
    const mergedItems: TemplateItem[] = [];

    const baselineItemIdsForSection = new Set(
      baselineSection.items.map(item => item.id)
    );

    for (const baselineItem of sortByOrder(baselineSection.items)) {
      const draftItem = draftItemById.get(baselineItem.id);
      if (draftItem) {
        mergedItems.push({ ...draftItem });
      } else {
        mergedItems.push({ ...baselineItem });
        removedOverlayItemIds.add(baselineItem.id);
      }
    }

    const draftOnlyItems = sortByOrder(
      draftSection.items.filter(item => !baselineItemIdsForSection.has(item.id))
    );

    mergedItems.push(...draftOnlyItems.map(item => ({ ...item })));

    sortedSections.push({
      ...draftSection,
      items: mergedItems,
      order: draftSection.order,
    });
  }

  const draftOnlySections = sortByOrder(
    draft.sections.filter(section => !baselineSectionIds.has(section.id))
  );
  for (const section of draftOnlySections) {
    sortedSections.push(cloneTemplateSection(section));
  }

  return {
    sortedSections,
    removedOverlayItemIds,
    removedSectionPresentationIds,
  };
}
